import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import PageTitle from "../store/PageTitle";
import airtelIcon from "../home/img/white-1024x768.jpg";
import mtnIcon from "../home/img/mtn-money.png";
// import zamtelIcon from "../home/img/ZamtelLogo_White-m85aPxq2.png";
import Loader from "../home/Loader";
import axios from "axios";

const serverUrl = process.env.REACT_APP_SERVER_URL;

// const publicKey = process.env.REACT_APP_LENCO_PUBLIC_KEY;

const encryptCardData = async (cardDetails, publicKey) => {
  if (!publicKey) {
    throw new Error("Missing public key for card encryption");
  }

  return JSON.stringify(cardDetails);
};

const Payment = ({ toggleLogin }) => {
  // const [paymentType] = useState("mobile-money");
  const user = useSelector((state) => state.auth.user);
  const [phone, setPhone] = useState(""); // For mobile money
  const location = useLocation();
  const { paymentDetails } = location.state || {};
  const navigate = useNavigate;

  const token = useSelector((state) => state.auth.token);
  const [paymentMethod, setPaymentMethod] = useState("mobile"); // 'mobile' or 'card'
  const [cardDetails, setCardDetails] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
  });

  const user_avatar =
    user?.identities?.find((identity) => identity?.provider === "google")
      ?.identity_data?.avatar_url || null;

  const [loading, setLoading] = useState(false);
  const MOBILE_MONEY_MAX_AMOUNT = 10000;
  const CARD_PAYMENTS_UNAVAILABLE = true;

  const validatePhone = (phone) => {
    return /^0\d{9}$/.test(phone);
  };

  const validateMobilePaymentAmount = (amount) => {
    const numericAmount = Number(amount);

    if (!amount || Number.isNaN(numericAmount)) {
      toast.error("Please enter a valid amount");
      return false;
    }

    if (numericAmount > MOBILE_MONEY_MAX_AMOUNT) {
      toast.error("Mobile money transactions cannot exceed 10,000 ZMW");
      return false;
    }

    return true;
  };

  // New Validation logic
  const validateCard = () => {
    if (!cardDetails.name.trim()) {
      toast.error("Cardholder name required");
      return false;
    }

    if (cardDetails.number.replace(/\s/g, "").length < 16) {
      toast.error("Invalid card number");
      return false;
    }

    if (!/^\d{2}\/\d{2}$/.test(cardDetails.expiry)) {
      toast.error("Invalid expiry date");
      return false;
    }

    if (!/^\d{3,4}$/.test(cardDetails.cvv)) {
      toast.error("Invalid CVV");
      return false;
    }

    return true;
  };

  // New endpoint
  const createOrder = async (referenceNo) => {
    const orderData = {
      user_infor: paymentDetails?.user,
      user_avatar,
      checkoutItems: paymentDetails?.items,
      totalAmount: paymentDetails?.payment?.total,
      deliveryAddress: paymentDetails?.delivery,
      deliveryMethod: paymentDetails?.deliveryMethod,
      paymentMethod: paymentMethod === "mobile" ? "mobile-money" : "card",
      paymentStatus: "pending",
      paymentReference: referenceNo,
    };

    const response = await axios.post(`${serverUrl}/create-order`, orderData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    toast.success("Order created successfully, initiating payment");

    if (!response.data.success) {
      throw new Error("Order creation failed");
    }

    return response.data.orderId;
  };

  //payment polling

  const statusPolling = async (orderId) => {
    const MAX_ATTEMPTS = 40;
    const POLL_INTERVAL = 3000; // Poll every 3 seconds

    let attempts = 0;

    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          attempts++;

          const response = await axios.get(
            `${serverUrl}/order-status/${orderId}`,
          );

          const { success, status, reasonForFailure } = response.data;

          if (!success) {
            clearInterval(interval);
            setLoading(false);

            toast.error("Unable to retrieve payment status.");
            return reject(new Error("Unable to retrieve payment status."));
          }

          console.log(
            `Polling order ${orderId}: ${status} (${attempts}/${MAX_ATTEMPTS})`,
          );

          // Still waiting for the gateway/webhook
          if (status === "pending") {
            if (attempts >= MAX_ATTEMPTS) {
              clearInterval(interval);
              setLoading(false);

              toast.error(
                "Payment confirmation timed out. Please check your order later.",
              );

              return reject(new Error("Polling timed out"));
            }

            return;
          }

          // Payment status has changed, stop polling
          clearInterval(interval);
          setLoading(false);

          if (status === "paid") {
            toast.success("Payment successful! Your order has been placed.");

            Swal.fire({
              icon: "success",
              title: "Payment Successful!",
              text: "Your order has been placed successfully.",
              showConfirmButton: true,
              confirmButtonText: "My Orders",
              showDenyButton: true,
              denyButtonText: "OK",
              denyButtonColor: "#6c757d",
              confirmButtonColor: "#198754",
            }).then((result) => {
              if (result.isConfirmed) {
                navigate("/OrdersTable");
              }
            });

            return resolve("paid");
          }

          if (status === "failed" || status === "cancelled") {
            const message =
              reasonForFailure ||
              "Your payment could not be processed. Please try again.";

            toast.error(message);

            Swal.fire({
              icon: "error",
              title: "Payment Failed",
              text: message,
              confirmButtonText: "OK",
              confirmButtonColor: "#dc3545",
            });

            return reject(new Error(message));
          }

          // Any unexpected status
          toast.error(`Unknown payment status: ${status}`);
          return reject(new Error(`Unknown payment status: ${status}`));
        } catch (err) {
          clearInterval(interval);
          setLoading(false);

          console.error("Polling error:", err);

          toast.error("Failed to check payment status.");

          return reject(err);
        }
      }, POLL_INTERVAL);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    try {
      if (paymentMethod === "mobile") {
        const amount = paymentDetails?.payment?.total;

        if (!validateMobilePaymentAmount(amount)) {
          return;
        }

        if (!validatePhone(phone)) {
          toast.error("Enter a valid mobile number");
          return;
        }
      }

      if (paymentMethod === "card") {
        if (!validateCard()) {
          return;
        }
      }

      setLoading(true);

      const referenceNo = `REF-${Date.now()}`;

      const orderId = await createOrder(referenceNo);

      let payload;

      if (paymentMethod === "mobile") {
        payload = {
          referenceNo,
          amount: paymentDetails?.payment?.total,
          currency: "ZMW",
          receiverId: phone,
          paymentType: "mobile-money",
          mobileMoneyProvider: mobileProvider.value,
          orderId,
          clientEmail: user?.email,
          clientName: paymentDetails?.user?.name,
        };
      } else {
        /**
         * Encrypt using Lenco public key
         */
        const encryptedCardData = await encryptCardData(
          cardDetails,
          process.env.REACT_APP_LENCO_PUBLIC_KEY,
        );

        payload = {
          referenceNo,
          amount: paymentDetails?.payment?.total,
          currency: "ZMW",
          orderId,
          clientEmail: user?.email,
          clientName: cardDetails.name,
          address: paymentDetails.delivery,
          encryptedCardData,
        };
      }

      const endpoint =
        paymentMethod === "mobile" ? "/mobile-payments" : "/card-payments";

      const paymentResponse = await axios.post(
        `${serverUrl}${endpoint}`,
        payload,
      );

      if (!paymentResponse.data) {
        throw new Error("Failed to initiate payment");
      }

      if (paymentMethod === "mobile") {
        toast.success(
          "Please check your phone and approve the payment prompt.",
        );
        await statusPolling(orderId); // start polling DB for status changes
      } else {
        toast.success("Payment initiated successfully");
      }

      if (!paymentResponse.data) {
        throw new Error("Failed to initiate payment");
      }

      toast.success("Payment initiated successfully");

      // pollPaymentStatus(orderId);
    } catch (error) {
      setLoading(false);

      if (error.response?.status === 401) {
        toast.error("Please log in again");

        toggleLogin();
        return;
      }

      toast.error(
        error.response?.data?.error || error.message || "Payment failed",
      );
    }
  };

  const paymentOptions = [
    {
      value: "airtel",
      label: "Airtel Mobile Money",
      icon: airtelIcon,
    },
    {
      value: "mtn",
      label: "MTN Mobile Money",
      icon: mtnIcon,
    },
  ];

  const [showDropdown, setShowDropdown] = useState(false);

  const [mobileProvider, setMobileProvider] = useState(paymentOptions[0]);

  const handlePaymentMethodChange = (method) => {
    if (method === "card" && CARD_PAYMENTS_UNAVAILABLE) {
      toast.warning(
        "Card payments are currently unavailable due to operator maintenance. Please use mobile money for now.",
      );
      return;
    }

    setPaymentMethod(method);
  };

  return (
    <div className="pay">
      <PageTitle title="Payment" />
      {loading && <Loader />}
      <div className="payment-container">
        <h2 className="payment-title">Payment Details</h2>

        {CARD_PAYMENTS_UNAVAILABLE && (
          <div className="maintenance-banner" role="alert">
            Card payments are currently unavailable due to operator maintenance.
            Please use mobile money for now.
          </div>
        )}

        {/* Slider Toggle */}
        <div className="payment-method-toggle">
          <div
            className={`toggle-slider ${paymentMethod === "card" ? "right" : "left"}`}
          />
          <button
            type="button"
            className={`toggle-option ${paymentMethod === "mobile" ? "active" : ""}`}
            onClick={() => handlePaymentMethodChange("mobile")}
          >
            Mobile Money
          </button>
          <button
            type="button"
            className={`toggle-option ${paymentMethod === "card" ? "active" : ""}`}
            onClick={() => handlePaymentMethodChange("card")}
            disabled={CARD_PAYMENTS_UNAVAILABLE}
          >
            Card
          </button>
        </div>

        <form onSubmit={handleSubmit} className="payment-form">
          <div className="form-group">
            <label htmlFor="amount">Amount</label>
            <input
              id="amount"
              type="number"
              className="form-control"
              value={paymentDetails?.payment?.total}
              disabled
            />
            {paymentMethod === "mobile" && (
              <small className="text-muted">
                Mobile money transactions are capped at 10,000 ZMW.
              </small>
            )}
          </div>

          {/* Mobile Money Section */}
          {paymentMethod === "mobile" && (
            <>
              <div className="form-group">
                <label htmlFor="payment-type">Payment Provider</label>
                <div className="custom-select" tabIndex={0}>
                  <div
                    className="selected-option"
                    onClick={() => setShowDropdown((prev) => !prev)}
                  >
                    <img
                      src={mobileProvider.icon}
                      alt=""
                      className="option-icon"
                    />
                    {mobileProvider.label}
                  </div>
                  {showDropdown && (
                    <div className="options-list">
                      {paymentOptions.map((option) => (
                        <div
                          key={option.value}
                          className="option-item"
                          onClick={() => {
                            setMobileProvider(option);
                            setShowDropdown(false);
                          }}
                        >
                          <img
                            src={option.icon}
                            alt=""
                            className="option-icon"
                          />
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <select
                  id="payment-type"
                  className="form-control"
                  value={mobileProvider.value}
                  style={{ display: "none" }}
                  readOnly
                >
                  {paymentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  type="tel"
                  className="form-control"
                  value={phone}
                  maxLength={10}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  placeholder="097XXXXXXX"
                  required
                />
              </div>
            </>
          )}

          {/* Card Section */}
          {paymentMethod === "card" && (
            <>
              <div className="form-group">
                <label htmlFor="card-name">Cardholder Name</label>
                <input
                  id="card-name"
                  type="text"
                  className="form-control"
                  value={cardDetails.name}
                  onChange={(e) =>
                    setCardDetails((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="card-number">Card Number</label>
                <input
                  id="card-number"
                  type="text"
                  className="form-control"
                  value={cardDetails.number}
                  onChange={(e) =>
                    setCardDetails((prev) => ({
                      ...prev,
                      number: e.target.value,
                    }))
                  }
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  required
                />
              </div>

              <div className="form-flex">
                <div
                  className="form-group"
                  style={{ flex: 1, marginRight: "1rem" }}
                >
                  <label htmlFor="card-expiry">Expiry Date</label>
                  <input
                    id="card-expiry"
                    type="text"
                    className="form-control"
                    value={cardDetails.expiry}
                    onChange={(e) =>
                      setCardDetails((prev) => ({
                        ...prev,
                        expiry: e.target.value,
                      }))
                    }
                    placeholder="MM/YY"
                    maxLength={5}
                    required
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="card-cvv">CVV</label>
                  <input
                    id="card-cvv"
                    type="text"
                    className="form-control"
                    value={cardDetails.cvv}
                    onChange={(e) =>
                      setCardDetails((prev) => ({
                        ...prev,
                        cvv: e.target.value,
                      }))
                    }
                    placeholder="123"
                    maxLength={4}
                    required
                  />
                </div>
              </div>
            </>
          )}

          <button type="submit" className="submit-button">
            Pay Now
          </button>
          <ToastContainer position="top-center" />
        </form>

        <style>{`
    .pay{
      margin:10%
    }
    .payment-container {
      max-width: 500px;
      margin: 0 auto;
      padding: 2rem;
      background-color: #ffffff;
      border-radius: 10px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    }
    
    .payment-title {
      color: #333;
      font-size: 1.5rem;
      margin-bottom: 1.5rem;
      font-weight: 600;
      text-align: center;
    }

    .maintenance-banner {
      background: #fff3cd;
      color: #856404;
      border: 1px solid #ffeeba;
      border-radius: 8px;
      padding: 0.85rem 1rem;
      margin-bottom: 1rem;
      font-size: 0.95rem;
      text-align: center;
    }

    /* Slider Toggle */
    .payment-method-toggle {
      position: relative;
      display: flex;
      background: #f0f4f8;
      border-radius: 8px;
      padding: 4px;
      margin-bottom: 1.5rem;
      overflow: hidden;
    }

    .toggle-slider {
      position: absolute;
      top: 4px;
      left: 4px;
      width: calc(50% - 4px);
      height: calc(100% - 8px);
      background: #3b82f6;
      border-radius: 6px;
      transition: transform 0.3s ease;
      z-index: 1;
    }

    .toggle-slider.right {
      transform: translateX(100%);
    }

    .toggle-option {
      position: relative;
      z-index: 2;
      flex: 1;
      padding: 0.6rem 1rem;
      border: none;
      background: transparent;
      font-size: 0.95rem;
      font-weight: 500;
      color: #4a5568;
      cursor: pointer;
      transition: color 0.3s ease;
      border-radius: 6px;
    }

    .toggle-option.active {
      color: #ffffff;
    }

    .toggle-option:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }
    
    .payment-form {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }
    .form-flex{
      display:flex;
      justify-content: space-between;
    }
    
    .form-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    
    label {
      font-size: 0.9rem;
      font-weight: 500;
      color: #4a5568;
    }
    
    .form-control {
      width: 100%;
      padding: 0.75rem 1rem;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 1rem;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    
    .form-control:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
    }
    
    .submit-button {
      margin-top: 0.75rem;
      padding: 0.75rem 1rem;
      background-color: #3b82f6;
      color: white;
      border: none;
      border-radius: 6px;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s;
    }
    
    .submit-button:hover {
      background-color: #2563eb;
    }
    
    .submit-button:active {
      background-color: #1d4ed8;
    }
    
    select.form-control {
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234a5568' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'%3E%3C/path%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 1rem center;
      background-size: 1rem;
      padding-right: 2.5rem;
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .input-wrapper input {
      padding-left: 40px;
    }

    .input-wrapper .icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: gray;
    }

    .custom-select {
      position: relative;
      width: 100%;
      cursor: pointer;
    }
    .selected-option, .option-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0.75rem 1rem;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      background: #fff;
    }
    .options-list {
      position: absolute;
      width: 100%;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      z-index: 10;
      margin-top: 2px;
    }
    .option-item:hover {
      background: #f0f4f8;
    }
    .option-icon {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }

    @media (max-width: 640px) {
      .payment-container {
        padding: 1.5rem;
        border-radius: 8px;
      }
    }
  `}</style>
      </div>
    </div>
  );
};

export default Payment;
